import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { GreenCheckAwsEnvironmentName } from '@gcv/shared';
import { useRollbar } from '@rollbar/react';
import { useRollbarPerson } from '@rollbar/react';
import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react';

import { UsersApi } from 'api';
import type { Auth0SessionToken } from 'api/api-util/api-auth0-session';
import ApiAuth0Session, { GCApplicationAccess } from 'api/api-util/api-auth0-session';
import { UserType } from 'domain/types/user';
import { environment } from 'environments/environment';
import {
  useAccountOwnerMetadata,
  useBank,
  useBankTemplates,
  useInvitedDispensaries,
  useLicenses,
  useMinifiedDispensaries,
  useMuos,
  useStaff,
  useTransactionCodes,
  useUser
} from 'hooks/queries';
import { getAppViewStateStore } from 'stores/AppViewStateStore';
import { getCommentStore } from 'stores/CommentStore';
import { getFiRelationshipManagerStore } from 'stores/FiRelationshipManagerStore';
import { getGcvAdminStore } from 'stores/GcvAdminStore';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { identifyLaunchDarklyUser } from 'third-party-integrations/launch-darkly';
import { initLogRocket, updateLogRocketConfig } from 'third-party-integrations/log-rocket';
import { validateRedirectUri } from 'ui/apps/shared/auth0/auth0-login';
import { Spinner } from 'ui/atoms';
import { initializeLoggedInUser } from 'ui/routing/router-util';
import { getRedirectURICache } from 'util/auth0-storage.util';
import { getUserStore } from '../../stores/UserStore';
import { CrbRoute } from './route-crb';
import { FiRoute } from './route-fi';
import { GcvRoute } from './route-gcv';

const Login = React.lazy(() => import('../apps/shared/auth0/auth0-login'));
const SSOFork = React.lazy(() => import('../apps/shared/auth0/auth0-sso-fork'));
const LoginSupport = React.lazy(() => import('../apps/shared/login-support'));
const Logout = React.lazy(() => import('../apps/shared/auth0/logout'));

const AppRouter = () => {
  const navigate = useNavigate();
  const rollbar = useRollbar();
  const location = useLocation();
  const queryClient = useQueryClient();

  const userApi = new UsersApi();

  const appViewStore = getAppViewStateStore();
  const gcvAdminStore = getGcvAdminStore();
  const userStore = getUserStore();
  const commentStore = getCommentStore();
  const snackbarStore = getSnackbarStore();
  const fiBankUserStore = getFiRelationshipManagerStore();

  const { isAuthenticated, isLoading: isLoadingAuth0, getAccessTokenSilently } = useAuth0();
  const [isLoggedInUserLoading, setIsLoggedInUserLoading] = React.useState(true);
  const [isCrbLoading, setIsCrbLoading] = React.useState(false);

  const { user, userBankId } = useUser();
  const { bank, isLoadingBank } = useBank();
  const { isLoadingMinifiedDispensaries } = useMinifiedDispensaries();
  const { isLoadingMuos } = useMuos();
  // shouldNotifyOnChange param is used to stop rerenders when not tracking any returned data
  // https://tanstack.com/query/latest/docs/framework/react/guides/render-optimizations#tracked-properties
  useLicenses({ shouldNotifyOnChange: false });
  useTransactionCodes({ shouldNotifyOnChange: false });
  useStaff({ shouldNotifyOnChange: false });
  useAccountOwnerMetadata({ shouldNotifyOnChange: false });
  useBankTemplates({ shouldNotifyOnChange: false });
  useInvitedDispensaries({ shouldNotifyOnChange: false });

  useRollbarPerson({
    id: user?.id,
    name: user?.firstName + ' ' + user?.lastName,
    email: user?.email
  });

  // since hooks cant be used within class based components,
  // so we inject the query client to the user store here
  // so we can invalidate the cache when the user is updated throughout the app
  React.useEffect(() => {
    userStore.setQueryClient(queryClient);
    fiBankUserStore.setQueryClient(queryClient);
  }, [queryClient]);

  React.useEffect(() => {
    // when a crb is invited and has just accepted the terms of service,
    // we want to show the loading spinner until the crb is fully initialized
    if (location.state?.from === 'tos') setIsCrbLoading(true);
  }, []);

  React.useEffect(() => {
    if (user?.id && !isLoadingAuth0) {
      setIsLoggedInUserLoading(false);
      updateLogRocketConfig(user);

      if (user?.invitation_status === 'archived') {
        snackbarStore.showErrorSnackbarMessage('Something went wrong. We were unable to log you in.');
        navigate('/logout');
      } else {
        userStore.isLoggedIn = true;
      }

      if (ApiAuth0Session.selectedApplication === GCApplicationAccess.All) {
        navigate('/sso');
      } else if (
        ApiAuth0Session.selectedApplication === GCApplicationAccess.GCV &&
        location.pathname !== '/sso' &&
        location.pathname !== '/logout'
      ) {
        initializeLoggedInUser(navigate, user, setIsCrbLoading);
      }
    }
  }, [user?.id]);

  const initializeUser = (token: Auth0SessionToken) => {
    ApiAuth0Session.updateToken(token);
    if (location.pathname === '/sso') {
      ApiAuth0Session.selectedApplication = ApiAuth0Session.userAccess;
    }

    // We update the token earlier this this function, ensuring userIdToken is populated
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const auth0Subject = ApiAuth0Session.userIdToken!.sub!;

    // Format: "auth0|<user_id>". Users in GCV / PQ
    if (auth0Subject.includes('auth0|')) {
      // else get user by auth0, that call does the create if not exist
      const userId = auth0Subject.split('|')[1];
      localStorage.setItem('userId', userId);
    } else {
      // Format: "oidc|Berkshire|00u9mkb3h53KpRsfo4x7" Users from 3rd party bank
      userApi.obtainSsoUser(auth0Subject).then((ssoUser) => {
        localStorage.setItem('userId', ssoUser?.id);
      });
    }

    appViewStore.setReadyToLoadUser(true);
  };

  const cacheRedirectUrl = () => {
    const redirect_uri = new URLSearchParams(location.search).get('redirect_uri');
    if (redirect_uri && location.pathname === '/authorize' && validateRedirectUri(redirect_uri)) {
      getRedirectURICache().cacheRedirectURI(redirect_uri);
    }

    const returnTo_uri = new URLSearchParams(location.search).get('returnTo');
    if (returnTo_uri && location.pathname === '/logout' && validateRedirectUri(returnTo_uri)) {
      getRedirectURICache().cacheReturnToURI(returnTo_uri);
    }
  };

  React.useEffect(() => {
    // reset the current comment if comments are not open on page navigation
    return () => {
      if (!commentStore.commentsOpen) {
        commentStore.setCurrentPost(null);
      }
    };
  }, [location]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const isInCypressTestMode = environment.env !== GreenCheckAwsEnvironmentName.PROD && window.Cypress;

  if (isInCypressTestMode) {
    // When testing via Cypress, use programmatic login
    // https://docs.cypress.io/guides/end-to-end-testing/auth0-authentication#Adapting-the-front-end
    React.useEffect(() => {
      const auth0 = JSON.parse(localStorage.getItem('auth0Cypress')!);
      if (auth0) {
        initializeUser(auth0.body);
        setIsCrbLoading(true);
      } else {
        setIsLoggedInUserLoading(false);
        navigate('/');
      }
    }, []);
  } else {
    React.useEffect(() => {
      if (isLoadingAuth0) {
        return;
      }

      if (window.location.href.indexOf('logout') > -1) {
        cacheRedirectUrl();
        setIsLoggedInUserLoading(false);
        return;
      }

      if (!isLoadingAuth0 && isAuthenticated) {
        getAccessTokenSilently({ detailedResponse: true }).then((token) => {
          initializeUser(token);
        });
      } else {
        cacheRedirectUrl();
        setIsLoggedInUserLoading(false);
        navigate('/');
      }
    }, [isAuthenticated, isLoadingAuth0]);
  }

  React.useEffect(() => {
    initLogRocket(rollbar);
  }, []);

  const isLoadingBankApp = () => {
    if (!userBankId) return false;

    return isLoadingBank || isLoadingMinifiedDispensaries || isLoadingMuos;
  };

  const isLoadingCrbApp = () => {
    // once we refactor the CRB side of the app we can check against crb id instead.
    if (userBankId) return false;

    return isCrbLoading;
  };

  const isLoadingGcvApp = () => {
    return user?.type === UserType.GCV && !gcvAdminStore.isLoaded;
  };

  if (isLoggedInUserLoading || isLoadingBankApp() || isLoadingCrbApp() || isLoadingGcvApp()) {
    return <Spinner text={isLoggedInUserLoading ? 'Logging in...' : 'Loading business information..'} />;
  }

  // Set up LaunchDarkly targeting for FI users and orgs
  if (user && bank) {
    identifyLaunchDarklyUser(user.id, user.email, user.firstName, user.lastName, bank.id, bank.orgName);
  }

  const path = location.pathname;
  const RoutesRender = () => {
    if (path === '/sso') {
      return <SSOFork />;
    } else if (path === '/logout') {
      return <Logout />;
    } else if (path === '/login/support') {
      return <LoginSupport />;
    } else if (path.includes('/crb/')) {
      return <CrbRoute path={path} />;
    } else if (path.includes('/fi/')) {
      return <FiRoute path={path} />;
    } else if (path.includes('/gcv/')) {
      return <GcvRoute />;
    } else {
      return <Login />;
    }
  };

  return <RoutesRender />;
};

export default observer(AppRouter);
